/**
 * HOCP Operational Design System — v1
 * ────────────────────────────────────────────────────────────────────
 * Design tokens shared by every operational primitive.
 *
 * SCOPE: Operational modules only (Front Desk, Housekeeping, Engineering,
 * Restaurant, and future Pool / Laundry / Procurement / Inventory).
 * NOT for administrative screens (Master Data, Reports, Rules, Audit).
 *
 * Locked principles (see ADR-014):
 *   - Large touch targets (min 48×48 px for taps, primary CTAs ≥56 px)
 *   - Colour + Text + Icon on every status (never colour alone)
 *   - No hover-only affordances, no double-clicks, no right-click menus
 *   - Progressive disclosure (details in secondary views)
 *   - i18n-ready: every visible string flows through a `label` prop
 *   - Three-interaction rule for common tasks where reasonably possible
 */
import {
  CircleCheck, PlayCircle, Clock, AlertTriangle, AlertOctagon, Ban,
  HelpCircle,
} from "lucide-react";

// ── Canonical Status Tokens (locked by user) ────────────────────────
// Every operational state MUST map to exactly one of these.
export const STATUS_TOKENS = {
  ready: {
    key: "ready",
    label: "Ready",
    aliases: ["available", "open", "clean", "checked_out", "completed"],
    icon: CircleCheck,
    // Tailwind — bg / border / text / dot
    tone: {
      bg: "bg-emerald-50",
      border: "border-emerald-400",
      text: "text-emerald-800",
      dot: "bg-emerald-500",
      hoverBorder: "hover:border-emerald-600",
    },
  },
  active: {
    key: "active",
    label: "Active",
    aliases: ["occupied", "in_progress", "seated", "in_kitchen", "checked_in", "confirmed", "walk_in"],
    icon: PlayCircle,
    tone: {
      bg: "bg-sky-50",
      border: "border-sky-500",
      text: "text-sky-800",
      dot: "bg-sky-500",
      hoverBorder: "hover:border-sky-700",
    },
  },
  pending: {
    key: "pending",
    label: "Pending",
    aliases: ["waiting", "cleaning", "awaiting", "dirty", "draft"],
    icon: Clock,
    tone: {
      bg: "bg-amber-50",
      border: "border-amber-400",
      text: "text-amber-800",
      dot: "bg-amber-500",
      hoverBorder: "hover:border-amber-600",
    },
  },
  attention: {
    key: "attention",
    label: "Needs Attention",
    aliases: ["inspection", "delayed", "warning", "hold"],
    icon: AlertTriangle,
    tone: {
      bg: "bg-orange-50",
      border: "border-orange-500",
      text: "text-orange-800",
      dot: "bg-orange-500",
      hoverBorder: "hover:border-orange-700",
    },
  },
  critical: {
    key: "critical",
    label: "Critical",
    aliases: ["out_of_service", "error", "emergency", "failed"],
    icon: AlertOctagon,
    tone: {
      bg: "bg-red-50",
      border: "border-red-500",
      text: "text-red-800",
      dot: "bg-red-500",
      hoverBorder: "hover:border-red-700",
    },
  },
  blocked: {
    key: "blocked",
    label: "Blocked",
    aliases: ["closed", "voided", "cancelled", "disabled", "no_show"],
    icon: Ban,
    tone: {
      bg: "bg-neutral-100",
      border: "border-neutral-500",
      text: "text-neutral-700",
      dot: "bg-neutral-500",
      hoverBorder: "hover:border-neutral-700",
    },
  },
  unknown: {
    key: "unknown",
    label: "Unknown",
    aliases: ["n/a", "unset"],
    icon: HelpCircle,
    tone: {
      bg: "bg-slate-50",
      border: "border-slate-400",
      text: "text-slate-700",
      dot: "bg-slate-400",
      hoverBorder: "hover:border-slate-600",
    },
  },
};

// Alias resolver — accepts any raw state string (available / seated / dirty / …)
// and returns the canonical token key.
const ALIAS_INDEX = (() => {
  const m = {};
  for (const [key, tok] of Object.entries(STATUS_TOKENS)) {
    m[key] = key;
    for (const a of tok.aliases) m[a] = key;
  }
  return m;
})();

export function resolveStatusKey(raw) {
  if (!raw) return "unknown";
  const k = String(raw).toLowerCase();
  return ALIAS_INDEX[k] || "unknown";
}

export function getStatusToken(raw) {
  return STATUS_TOKENS[resolveStatusKey(raw)];
}

// ── Sizing (touch targets) ──────────────────────────────────────────
// Primary CTAs 56 px · Standard touch 48 px · Secondary 44 px.
export const TOUCH = {
  primary:   "h-14 min-w-[56px] text-[16px]",     // 56px
  standard:  "h-12 min-w-[48px] text-[15px]",     // 48px — default operational
  secondary: "h-11 min-w-[44px] text-[14px]",     // 44px — for chips/small
};

// ── Typography ──────────────────────────────────────────────────────
export const TEXT = {
  hero:    "text-[28px] font-bold font-display leading-tight",
  card:    "text-[18px] font-semibold leading-tight",
  body:    "text-[15px] leading-normal",
  metaLg:  "text-[13px]",
  metaSm:  "text-[12px] uppercase tracking-wider",
};

// ── i18n helper stub ────────────────────────────────────────────────
// v1 is English-only, but every component must accept its visible strings via
// `label` / `sublabel` props (never hard-coded). This stub lets consumers
// wire in a full i18n backend later without touching primitive callers.
export function t(key, fallback) {
  // TODO(v2): swap in react-i18next / lingui / self-hosted messages
  return fallback ?? key;
}
